import React, { useState } from "react";
import styled from "@emotion/styled";
import axios from "axios";
import { darken } from "polished";
import { customBlue } from "../../theme/variants";

import { Search as SearchIcon } from "@mui/icons-material";
import { Button, Card, InputBase } from "@mui/material";

import {
  refreshSelectedPublications,
  setPopualateEngine,
  setPopulateQuery,
  setPublications,
} from "../../redux/slices/populate";
import { RootState } from "../../redux/store";

import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import PopulateResultsCard from "../../components/pages/populate/PopulateResultsCard";
import Loader from "../../components/Loader";
import NoResultsFound from "../../components/pages/NoResultsFound";
import { SnackbarFns, useFileUpload } from "../../utils/fnsRedux";
import { auth } from "../../firebase/init";
import { Publication, UploadStatus } from "../../types/app";

const Main = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SearchEngine = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
`;

const EngineButton = styled(Button)<{ selected: boolean }>`
  border-radius: 10px;
  padding: 5px 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  background-color: ${(props) =>
    props.selected
      ? props.theme.palette.primary.main
      : props.theme.palette.primary.contrastText};
  color: ${(props) =>
    props.selected
      ? props.theme.palette.primary.contrastText
      : props.theme.palette.primary.main};

  &:hover {
    background-color: ${(props) =>
      props.selected ? props.theme.palette.primary.main : customBlue[400]};
    color: ${(props) =>
      props.selected
        ? props.theme.palette.primary.contrastText
        : props.theme.palette.secondary.contrastText};
  }
`;

const SearchCard = styled(Card)`
  height: 60px;
  width: 100%;
  max-width: 1200px;
  margin-top: 10px;
  border-radius: 15px;
  padding: 12px 15px;
  box-shadow: 0px 0px 5px -1px rgba(0, 0, 0, 0.2);
`;

const BodyCard = styled(SearchCard)`
  height: 100%;
  margin-top: 25px;
`;

const PopulateResults = styled.div`
  height: calc(100% - ${50}px);
  width: 100%;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Body = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: end;
`;

const Search = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up("xs")} {
    display: block;
  }
`;

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Input = styled(InputBase)`
  color: inherit;
  width: 100%;
  height: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
    width: 100%;
  }
`;

function PopulatePage() {
  const state = useAppSelector((state: RootState) => state.populate);
  const dispatch = useAppDispatch();
  const query = state.populateQuery;
  const [loading, setLoading] = useState(false);
  const [canPopulate, setCanPopulate] = useState(true);
  const [searchEngine, setSearchEngine] = useState("Google Scholar");
  const handleFileUpload = useFileUpload();
  const handleSnackbar = SnackbarFns();

  const handleSearch = async () => {
    setLoading(true);
    dispatch(refreshSelectedPublications());
    if (searchEngine === "Google Scholar") {
      const url = `https://scidirect-4oaufgem3a-uc.a.run.app/fetchGoogleScholar?query=${query}`;
      // const url = `https://us-central1-docuai-c7a15.cloudfunctions.net/main-staging/googlescholar?query=${query}&numResults=10`;
      const response = await axios.get(url);
      dispatch(setPublications(response.data));
      dispatch(setPopualateEngine("Google Scholar"));
    } else {
      const url = `https://scidirect-4oaufgem3a-uc.a.run.app/fetchScienceDirect?query=${query}`;
      // const url = `http://localhost:4000/fetchScienceDirect?query=${query}`;
      const response = await axios.get(url);
      const formattedResponse = response.data.slice(0, 9).map((entry: any) => ({
        title: entry.title,
        authors: entry.authors,
        pubYear: entry.date,
        venue: entry.publisher,
        abstract: "",
        pubUrl: entry.link,
        numCitations: 0,
        ePrintUrl: entry.link,
      }));
      dispatch(setPublications(formattedResponse));
      dispatch(setPopualateEngine("Science Direct"));
    }
    setLoading(false);
  };

  const populate = async () => {
    setCanPopulate(false);
    if (state.populateEngine === "Google Scholar") {
      const publications = state.selectedPublications.map(
        (idx) => state.publications[idx]
      );
      handleFileUpload("web", publications);
    } else {
      handleSnackbar("warning" as UploadStatus, "Uploading files...", true);
      const uid = auth.currentUser?.uid;
      let error = false;
      const urlData = [[] as Publication[], [] as Publication[]];

      state.selectedPublications.forEach((idx, index) => {
        const publication = state.publications[idx];
        const urlIndex = index % 2;
        urlData[urlIndex].push(publication);
      });

      const urlPromises = urlData.map(async (data, index) => {
        if (data.length > 0) {
          const urls = data.map((item) => item.pubUrl).join("--");
          const titles = data.map((item) => item.title).join("--");
          const updatedUrl = `https://scidirect-4oaufgem3a-uc.a.run.app/download?url=${urls}&filename=${titles}&uuid=${uid}&maxStorage=50&maxFile=10&acc=${index}`;
          // const updatedUrl = `http://localhost:4000/download?url=${urls}&filename=${titles}&uuid=${uid}&maxStorage=50&maxFile=10&acc=${index}`;
          await axios.get(updatedUrl).catch((err) => {
            handleSnackbar("error", err.response.data, true);
            error = true;
          });
        }
      });

      await Promise.all(urlPromises);

      if (!error) {
        handleSnackbar("success", "Successfully uploaded files", true);
      } else {
        handleSnackbar("success", "Successfully uploaded some files", true);
      }
    }
    dispatch(refreshSelectedPublications());
    setCanPopulate(true);
  };

  return (
    <Main>
      <SearchEngine>
        <EngineButton
          selected={searchEngine === "Google Scholar"}
          onClick={() => setSearchEngine("Google Scholar")}
        >
          Google Scholar
        </EngineButton>
        <EngineButton
          selected={searchEngine === "Science Direct"}
          onClick={() => setSearchEngine("Science Direct")}
          sx={{ marginLeft: "10px" }}
        >
          Science Direct
        </EngineButton>
      </SearchEngine>
      <SearchCard>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <Input
            placeholder={`Query for publications from ${searchEngine}`}
            value={query}
            onChange={(e) => dispatch(setPopulateQuery(e.target.value))}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
        </Search>
      </SearchCard>
      <BodyCard>
        {loading ? (
          <Loader />
        ) : state.publications.length === 0 ? (
          <NoResultsFound />
        ) : (
          <Body>
            <PopulateResults>
              {state.publications.map((publication, idx) => {
                return (
                  <PopulateResultsCard
                    key={idx}
                    idx={idx}
                    publication={publication}
                  />
                );
              })}
            </PopulateResults>
            <Button
              variant="contained"
              disabled={!canPopulate}
              sx={{ marginTop: "10px" }}
              onClick={populate}
            >
              Populate Docs
            </Button>
          </Body>
        )}
      </BodyCard>
    </Main>
  );
}

export default PopulatePage;
